import { useEffect, useState } from "react";
import { createNotifications, getUsersNotifications } from "src/apis/notificationsApi";
import { formatDateNotifications } from "src/helpers/formatDate";
import { useAppSelector } from "src/hooks/redux";
import { selectUser } from "src/redux/user/userSlice";
import * as yup from 'yup';


interface DataFilters {
    user_type?: string;
    user_source?: string;
    custom_query?: string;
    featuredMin?: string;
    featuredMax?: string;
    propertiesActiveMin?: string;
    propertiesActiveMax?: string;
    leadsMin?: string;
    leadsMax?: string;
    leadsDateMin?: Date;
    leadsDateMax?: Date;
}
interface NotificationForm {
    title: string;
    description: string;
    ctaText: string;
    ctaLink: string;
    notificationType: 'normal' | 'destacada';
    selectedUserTypes: string[];
    selectedUserClassifications: string[];
    customQuery: string;
}

const initialDataForm: NotificationForm = {
    title: '',
    description: '',
    ctaText: '',
    ctaLink: '',
    notificationType: 'normal' as 'normal' | 'destacada',
    selectedUserTypes: [] as string[],
    selectedUserClassifications: [] as string[],
    customQuery: '',
};

const initialDataFilters: DataFilters = {
    user_source: 'feed',
    featuredMin: '0',
    featuredMax: '10',
    propertiesActiveMin: '1',
    propertiesActiveMax: '100',
    leadsMin: '1',
    leadsMax: '100',
    leadsDateMin: new Date(),
    leadsDateMax: new Date(),
}

export const useNotifications = () => {
    const user = useAppSelector(selectUser);
    const [dataForm, setDataForm] = useState<NotificationForm>(initialDataForm);
    const [dataFilters, setDataFilters] = useState<DataFilters>(initialDataFilters);
    const [selectedUserTypes, setSelectedUserTypes] = useState<string[]>([]);
    const [errors, setErrors] = useState<any>({});
    const [blur, setBlur] = useState({});
    const [isValid, setIsValid] = useState(false);
    const [isValidSql, setIsValidSql] = useState(false);
    const [numberUsers, setNumberUsers] = useState<any>({ data: { count_result: 0, count_query: '' } });
    const [loading, setLoading] = useState(false)
    const [hasFeatured, setHasFeatured] = useState(false);
    const [customQuery, setCustomQuery] = useState('');
    const [customQueryError, setCustomQueryError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [created, setCreated] = useState(false);


    const handleSetHasFeatured = (value) => {
        setHasFeatured(value.target.checked);
    }
    const handleUserTypeToggle = (typeId: string) => {
        if (selectedUserTypes.includes(typeId)) {
            setSelectedUserTypes(selectedUserTypes.filter(id => id !== typeId));
        } else {
            setSelectedUserTypes([...selectedUserTypes, typeId]);
        }
    };

    const handleChange = (name: string, value: any) => {
        setBlur({
            ...blur,
            [name]: value,
        });
        setDataForm({
            ...dataForm,
            [name]: value,
        });
    };

    const handleSetDataFilters = (name: string, value: any) => {
        if (name === 'user_source') {
            setCustomQuery("");
            setCustomQueryError("");
        }
        setDataFilters({
            ...dataFilters,
            [name]: value,
        })
    }


    const handleSetLeadsDate = (name: string, value: any) => {
        setDataFilters({
            ...dataFilters,
            [name]: new Date(value),
        })
    }

    const handleGetNumberUsers = async () => {
        setLoading(true)
        let payload: any = {}
        if (customQuery) {
            payload.custom_query = customQuery
        } else {
            payload = {
                ...dataFilters,
                user_source: dataFilters.user_source === 'feed' ? 1 : null,
                leadsDateMin: formatDateNotifications(dataFilters.leadsDateMin.toJSON()),
                leadsDateMax: formatDateNotifications(dataFilters.leadsDateMax.toJSON()),
            }
            // eslint-disable-next-line eqeqeq
            if (payload.user_source != 1) {
                delete payload.user_source
            }
            if (selectedUserTypes.length > 0) {
                payload.user_type = selectedUserTypes.toString()
            }
        }
        const numberUsers = await getUsersNotifications(payload, user.token);
        if (numberUsers.success) {
            setNumberUsers(numberUsers.data.count_result);
            handleChange('customQuery', numberUsers.data.query_main);
            setLoading(false);
        } else {
            setNumberUsers(0);
            setCustomQueryError(numberUsers.message);
            setLoading(false);
        }
    }

    const validateFormData = async () => {
        const schema = yup.object().shape({
            title: yup.string().required('El campo es requerido').min(5, 'El campo debe tener al  menos 5 carácteres').max(100, 'El campo no puede tener más de 100 caracteres'),
            description: yup.string().required('El campo es requerido').min(5, 'El campo debe tener al  menos 5 carácteres').max(100, 'El campo no puede tener más de 100 caracteres'),
            ctaText: yup
                .string()
                .when('notificationType', {
                    is: 'normal',
                    then: (schema) => schema.notRequired(),
                    otherwise: (schema) => schema
                        .required('El campo es requerido')
                        .min(5, 'El campo debe tener al menos 5 caracteres')
                        .max(12, 'El campo no puede tener más de 12 caracteres')
                }),
            ctaLink: yup.string().url('Debe ser una URL válida').required('El campo es requerido'),
            notificationType: yup.string().oneOf(['normal', 'destacada']).required('El campo es requerido'),
            hasFeatured: yup.boolean(),
            featuredMin: yup.number(),
            featuredMax: yup.number(),
            activeInventory: yup.boolean(),
            propertiesActiveMin: yup.number(),
            propertiesActiveMax: yup.number(),
            hasLeads: yup.boolean(),
            leadsMin: yup.number(),
            leadsMax: yup.number(),
            customQuery: yup.string().test(
                'custom-query-validation',
                'El campo requerido"',
                () => {
                    const { user_source } = dataFilters;
                    const isValid = !!(customQuery && customQuery.trim() !== '' && isValidSql)
                    const response = user_source !== 'custom-query' || isValid;
                    return response;
                }
            ),
        });
        try {
            await schema.validate(dataForm, { abortEarly: false });
            setIsValid(true);
            setErrors({});
        } catch (err) {
            setIsValid(false);
            setErrors(
                err.inner.reduce((acc: any, error: any) => {
                    acc[error.path] = error.message;
                    return acc;
                }, {})
            );
        }
    };

    const handleSubmit = async () => {
        setLoading(true)
        let stringDetail = {}
        const notificationObject: any = {
            template_title: dataForm.title,
            template_body: dataForm.description,
            cta_label: dataForm.ctaText,
        };

        if (dataForm.notificationType === "destacada") {
            notificationObject.gold = 1;
        }
        stringDetail = JSON.stringify(notificationObject);

        const payload = {
            name: 'fast_notification',
            template_title: dataForm.title,
            template_body: dataForm.description,
            detail: stringDetail,
            cta_label: dataForm.ctaText,
            icon_url: "https://propiedadescom.s3.us-east-1.amazonaws.com/mailing_images/notification_highlight_invite.png",
            redirect_url: dataForm.ctaLink,
            to_send: true,
            users_query: dataForm.customQuery,
            fast_message: true
        }
        await createNotifications(payload, user.token)
        setLoading(false)
        setCreated(true)
    };

    const handleResetForm = () => {
        setDataForm(initialDataForm)
        setDataFilters(initialDataFilters)
        setCustomQuery("");
        setCustomQueryError("");
        setCreated(false);
        handleCloseModal()
        setSelectedUserTypes([])
        setErrors({})
        setBlur({})
        setHasFeatured(false)
    }

    const handleChangeDataFilters = (name: string, value: any) => {
        handleSetDataFilters(name, formatDateNotifications(value))
    }


    const handleOpenModal = () => {
        handleGetNumberUsers()
        setShowModal(true)
    }
    const handleCloseModal = () => {
        setShowModal(false)
        setCreated(false)
        setCustomQueryError("")
    }

    useEffect(() => {
        if (selectedUserTypes.length > 0) {
            handleSetDataFilters('user_type', selectedUserTypes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUserTypes])

    useEffect(() => {
        validateFormData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataForm, dataFilters]);

    const validSqlInjection = () => {
        if (dataFilters.user_source === 'custom-query' &&
            customQuery.length > 0 &&
            customQuery.toLowerCase().includes('select') &&
            customQuery.toLowerCase().includes('from') &&
            customQuery.toLowerCase().includes('where') &&
            !customQuery.toLowerCase().includes('update') &&
            !customQuery.toLowerCase().includes('delete') &&
            !customQuery.toLowerCase().includes('drop') &&
            !customQuery.toLowerCase().includes('alter')
        ) {
            setIsValidSql(true);
        } else {
            setIsValidSql(false);
        }
    }

    useEffect(() => {
        validSqlInjection()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customQuery, dataFilters.user_source]);

    useEffect(() => {
        validateFormData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customQuery, isValidSql]);

    return {
        blur,
        dataForm,
        errors,
        isValid,
        loading,
        dataFilters,
        numberUsers,
        selectedUserTypes,
        customQuery,
        hasFeatured,
        showModal,
        created,
        customQueryError,
        handleCloseModal,
        handleResetForm,
        handleOpenModal,
        handleSetHasFeatured,
        setCustomQuery,
        setCustomQueryError,
        handleChange,
        handleSubmit,
        handleSetDataFilters,
        handleGetNumberUsers,
        handleUserTypeToggle,
        handleChangeDataFilters,
        handleSetLeadsDate
    };
}